import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import type { PropertyOffer } from "@/types/buyer";
import { 
  DollarSign, 
  Calendar, 
  MapPin, 
  Building2, 
  Phone, 
  Mail, 
  User,
  TrendingUp,
  Clock
} from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { toast } from "sonner";
import { propertyOffersService } from "@/services/propertyOffersService";

interface OfferDetailModalProps {
  offer: PropertyOffer | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onOfferUpdated?: () => void;
}

export const OfferDetailModal = ({ 
  offer, 
  open, 
  onOpenChange,
  onOfferUpdated 
}: OfferDetailModalProps) => {
  const [sellerResponse, setSellerResponse] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [confirmAction, setConfirmAction] = useState<'accept' | 'reject' | null>(null);

  if (!offer) return null;

  const property = offer.property;
  const buyer = offer.buyer_profile;

  const handleAccept = async () => {
    setIsProcessing(true);
    try {
      const { error } = await propertyOffersService.acceptOffer(offer.id, sellerResponse);
      if (error) throw error;
      
      toast.success("Offer accepted successfully!");
      onOpenChange(false);
      onOfferUpdated?.();
    } catch (error: any) {
      toast.error(error.message || "Failed to accept offer");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleReject = async () => {
    setIsProcessing(true);
    try {
      const { error } = await propertyOffersService.rejectOffer(offer.id, sellerResponse);
      if (error) throw error;
      
      toast.success("Offer rejected");
      onOpenChange(false);
      onOfferUpdated?.();
    } catch (error: any) {
      toast.error(error.message || "Failed to reject offer");
    } finally {
      setIsProcessing(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-500/10 text-yellow-600 dark:text-yellow-400';
      case 'accepted':
        return 'bg-green-500/10 text-green-600 dark:text-green-400';
      case 'rejected':
        return 'bg-red-500/10 text-red-600 dark:text-red-400';
      case 'withdrawn':
        return 'bg-gray-500/10 text-gray-600 dark:text-gray-400';
      default:
        return '';
    }
  };

  const percentDiff = property?.price ? 
    (((offer.offer_amount - property.price) / property.price) * 100).toFixed(1) : 
    null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-start justify-between">
            <div>
              <DialogTitle className="text-xl">Offer Details</DialogTitle>
              <DialogDescription>
                Review and respond to this property offer
              </DialogDescription>
            </div>
            <Badge className={getStatusColor(offer.status)} variant="secondary">
              {offer.status.charAt(0).toUpperCase() + offer.status.slice(1)}
            </Badge>
          </div>
        </DialogHeader>

        <div className="space-y-6">
          {/* Property Information */}
          <div>
            <h4 className="font-semibold mb-3 flex items-center gap-2">
              <MapPin className="h-4 w-4" />
              Property Information
            </h4>
            <div className="flex gap-4">
              <img 
                src={property?.image_url || '/placeholder.svg'}
                alt={property?.address}
                className="w-32 h-32 object-cover rounded-lg"
              />
              <div className="flex-1">
                <p className="font-medium">{property?.address}</p>
                <p className="text-sm text-muted-foreground">
                  {property?.city}, {property?.state} {property?.zip_code}
                </p>
                <div className="mt-2 flex items-center gap-4 text-sm">
                  <span>{property?.bedrooms} bed</span>
                  <span>{property?.bathrooms} bath</span>
                  <span>{property?.square_feet?.toLocaleString()} sqft</span>
                </div>
                <p className="mt-2 text-lg font-bold">
                  Listing: ${property?.price?.toLocaleString()}
                </p>
              </div>
            </div>
          </div>

          <Separator />

          {/* Offer Information */}
          <div>
            <h4 className="font-semibold mb-3 flex items-center gap-2">
              <DollarSign className="h-4 w-4" />
              Offer Details
            </h4>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <p className="text-sm text-muted-foreground">Offer Amount</p>
                <p className="text-2xl font-bold text-godyspo-gold">
                  ${offer.offer_amount.toLocaleString()}
                </p>
                {percentDiff && (
                  <p className={`text-sm ${parseFloat(percentDiff) < 0 ? 'text-red-500' : 'text-green-500'}`}>
                    {parseFloat(percentDiff) > 0 ? '+' : ''}{percentDiff}% vs asking price
                  </p>
                )}
              </div>
              <div className="space-y-1">
                <p className="text-sm text-muted-foreground">Desired Close Date</p>
                <p className="text-lg font-medium">
                  {new Date(offer.close_date).toLocaleDateString('en-US', {
                    month: 'long',
                    day: 'numeric',
                    year: 'numeric'
                  })}
                </p>
                <p className="text-sm text-muted-foreground">
                  {Math.ceil((new Date(offer.close_date).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24))} days from now
                </p>
              </div>
            </div>

            {offer.buyer_notes && (
              <div className="mt-4">
                <p className="text-sm text-muted-foreground mb-2">Buyer Notes:</p>
                <div className="bg-muted/50 p-3 rounded-lg">
                  <p className="text-sm">{offer.buyer_notes}</p>
                </div>
              </div>
            )}
          </div>

          <Separator />

          {/* Buyer Information */}
          <div>
            <h4 className="font-semibold mb-3 flex items-center gap-2">
              <User className="h-4 w-4" />
              Buyer Information
            </h4>
            <div className="flex items-center gap-4 mb-4">
              <Avatar className="h-12 w-12">
                <AvatarImage src={buyer?.profile_image_url || ''} />
                <AvatarFallback className="bg-godyspo-blue text-white">
                  {buyer?.full_name?.split(' ').map(n => n[0]).join('') || 'B'}
                </AvatarFallback>
              </Avatar>
              <div>
                <p className="font-medium">{buyer?.full_name}</p>
                {buyer?.company_name && (
                  <p className="text-sm text-muted-foreground">{buyer.company_name}</p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-muted-foreground">Deals Annually</p>
                <p className="font-medium">{buyer?.deals_annually || 'N/A'}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Avg Close Time</p>
                <p className="font-medium">{buyer?.average_close_time || 'N/A'}</p>
              </div>
            </div>

            {buyer?.preferred_markets && Array.isArray(buyer.preferred_markets) && buyer.preferred_markets.length > 0 && (
              <div className="mt-3">
                <p className="text-sm text-muted-foreground mb-1">Preferred Markets</p>
                <p className="text-sm">{buyer.preferred_markets.join(', ')}</p>
              </div>
            )}

            <div className="mt-4 flex gap-2">
              {buyer?.phone_number && (
                <Button variant="outline" size="sm" asChild className="flex-1">
                  <a href={`tel:${buyer.phone_number}`}>
                    <Phone className="h-3 w-3 mr-1" />
                    Call
                  </a>
                </Button>
              )}
            </div>
          </div>

          {/* Timeline */}
          <div className="text-xs text-muted-foreground">
            <div className="flex items-center gap-2">
              <Clock className="h-3 w-3" />
              <span>Submitted {formatDistanceToNow(new Date(offer.created_at), { addSuffix: true })}</span>
            </div>
            {offer.responded_at && (
              <div className="flex items-center gap-2 mt-1">
                <Clock className="h-3 w-3" />
                <span>Responded {formatDistanceToNow(new Date(offer.responded_at), { addSuffix: true })}</span>
              </div>
            )}
          </div>

          {/* Seller response section - only for pending offers */}
          {offer.status === 'pending' && (
            <>
              <Separator />
              <div>
                <Label htmlFor="seller-response">Your Response (Optional)</Label>
                <Textarea
                  id="seller-response"
                  placeholder="Add a message to the buyer..."
                  value={sellerResponse}
                  onChange={(e) => setSellerResponse(e.target.value)}
                  maxLength={500}
                  rows={3}
                  className="mt-2"
                />
                <p className="text-xs text-muted-foreground text-right mt-1">
                  {sellerResponse.length}/500
                </p>
              </div>
            </>
          )}

          {/* Show seller response if offer has been responded to */}
          {offer.seller_response && offer.status !== 'pending' && (
            <>
              <Separator />
              <div>
                <p className="text-sm text-muted-foreground mb-2">Your Response:</p>
                <div className="bg-muted/50 p-3 rounded-lg">
                  <p className="text-sm">{offer.seller_response}</p>
                </div>
              </div>
            </>
          )}
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          {offer.status === 'pending' ? (
            <>
              <Button
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isProcessing}
              >
                Close
              </Button>
              <Button
                variant="destructive"
                onClick={() => setConfirmAction('reject')}
                disabled={isProcessing}
              >
                Reject Offer
              </Button>
              <Button
                onClick={() => setConfirmAction('accept')}
                disabled={isProcessing}
                className="bg-green-600 text-white hover:bg-green-700"
              >
                {isProcessing ? "Processing..." : "Accept Offer"}
              </Button>
            </>
          ) : (
            <Button onClick={() => onOpenChange(false)}>
              Close
            </Button>
          )}
        </DialogFooter>
      </DialogContent>

      {/* Accept Confirmation Dialog */}
      <AlertDialog open={confirmAction === 'accept'} onOpenChange={(open) => !open && setConfirmAction(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Accept this offer?</AlertDialogTitle>
            <AlertDialogDescription>
              You're about to accept an offer of <strong>${offer.offer_amount.toLocaleString()}</strong> for the property at <strong>{property?.address}</strong>.
              <br /><br />
              The buyer will be notified immediately. This action confirms your acceptance of this offer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                handleAccept();
                setConfirmAction(null);
              }}
              className="bg-green-600 text-white hover:bg-green-700"
            >
              Confirm Accept
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Reject Confirmation Dialog */}
      <AlertDialog open={confirmAction === 'reject'} onOpenChange={(open) => !open && setConfirmAction(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reject this offer?</AlertDialogTitle>
            <AlertDialogDescription>
              You're about to reject an offer of <strong>${offer.offer_amount.toLocaleString()}</strong> for the property at <strong>{property?.address}</strong>.
              <br /><br />
              The buyer will be notified of the rejection. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                handleReject();
                setConfirmAction(null);
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Confirm Reject
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
};
